import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { Home } from './pages/Home';
import { Products } from './pages/Products';
import { ProductDetail } from './pages/ProductDetail';
import { Cart } from './pages/Cart';
import { Checkout } from './pages/CheckoutWithShipping';
import { Wholesale } from './pages/Wholesale';
import { About } from './pages/About';
import { Contact } from './pages/Contact';
import { TrackOrder } from './pages/TrackOrder';
import { Admin } from './pages/Admin';
import { AdminLogin } from './pages/AdminLogin';
import { AdminInquiries } from './pages/AdminInquiries';
import { AdminCoupons } from './pages/AdminCoupons';
import { AdminOrders } from './pages/AdminOrders';
import { ProtectedAdminRoute } from './components/ProtectedAdminRoute';
import './App.css';

function App() {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('krishna-sugandh-cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('krishna-sugandh-cart', JSON.stringify(cart));
  }, [cart]);

  // Re-sync every cart item with live DB product (image, name, price, stock)
  // so admin updates are reflected instantly everywhere — no deploy / no cache.
  const refreshCart = useCallback(async () => {
    if (!cart.length) return;
    try {
      const { data: products } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/products`
      );
      const byId = new Map(products.map(p => [p.id, p]));
      let removed = 0;
      const next = [];
      for (const item of cart) {
        const fresh = byId.get(item.id);
        if (!fresh) { removed += 1; continue; }
        // Resolve the current variant price for the size the customer picked.
        // Match by both size and unit when possible (legacy cart may have no unit).
        const variant = (fresh.variants || []).find(v => {
          const combined = `${v.size} ${v.unit || ''}`.trim();
          return v.size === item.selectedSize
              || combined === item.selectedSize
              || `${v.size}${v.unit || ''}` === item.selectedSize;
        });
        const livePrice = variant?.price ?? fresh.price ?? item.price;
        next.push({
          ...item,
          name: fresh.name,
          displayName: item.selectedSize ? `${fresh.name} (${item.selectedSize})` : fresh.name,
          category: fresh.category,
          image: fresh.image,
          price: livePrice,
        });
      }
      // Only update state if anything actually changed to avoid re-render loops
      const changed = removed > 0 || next.some((n, i) => (
        n.name !== cart[i]?.name ||
        n.image !== cart[i]?.image ||
        n.price !== cart[i]?.price
      ));
      if (changed) {
        setCart(next);
        if (removed > 0) {
          toast.info(`${removed} unavailable item${removed === 1 ? '' : 's'} removed from your cart`);
        }
      }
    } catch (e) { /* network error — keep existing cart as-is */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.length, cart.map(c => c.id + ':' + (c.selectedSize || '')).join('|')]);

  // Refresh once on mount
  useEffect(() => { refreshCart(); /* eslint-disable-next-line */ }, []);
  // Refresh whenever tab regains focus (admin updated product in another tab)
  useEffect(() => {
    const onFocus = () => refreshCart();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => !document.hidden && refreshCart());
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshCart]);

  const handleAddToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1, cartItemId: Date.now() + Math.random() }]);
    }
  };

  const handleUpdateQuantity = (cartItemId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveItem(cartItemId);
    } else {
      setCart(cart.map(item =>
        item.cartItemId === cartItemId ? { ...item, quantity: newQuantity } : item
      ));
    }
  };

  const handleRemoveItem = (cartItemId) => {
    setCart(cart.filter(item => item.cartItemId !== cartItemId));
  };

  const handleClearCart = () => {
    setCart([]);
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <BrowserRouter>
      <div className="App">
        <Navbar cartCount={cartCount} />
        <Routes>
          <Route path="/" element={<Home onAddToCart={handleAddToCart} />} />
          <Route path="/products" element={<Products onAddToCart={handleAddToCart} />} />
          <Route path="/products/:id" element={<ProductDetail onAddToCart={handleAddToCart} />} />
          <Route 
            path="/cart" 
            element={
              <Cart 
                cart={cart} 
                onUpdateQuantity={handleUpdateQuantity} 
                onRemoveItem={handleRemoveItem} 
              />
            } 
          />
          <Route 
            path="/checkout" 
            element={<Checkout cart={cart} onClearCart={handleClearCart} />} 
          />
          <Route path="/wholesale" element={<Wholesale />} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedAdminRoute><Admin /></ProtectedAdminRoute>} />
          <Route path="/admin/orders" element={<ProtectedAdminRoute><AdminOrders /></ProtectedAdminRoute>} />
          <Route path="/admin/inquiries" element={<ProtectedAdminRoute><AdminInquiries /></ProtectedAdminRoute>} />
          <Route path="/admin/coupons" element={<ProtectedAdminRoute><AdminCoupons /></ProtectedAdminRoute>} />
        </Routes>
        <Footer />
        <Toaster position="top-center" theme="dark" richColors />
      </div>
    </BrowserRouter>
  );
}

export default App;